import { useEffect, useState, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Search as SearchIcon, X } from "lucide-react";
import { api } from "@/lib/api";
import { Eyebrow } from "@/components/common";

const WORK_LABEL = { "holy-book": "The Holy Book", "indic-civilisation": "Indic Civilisation", "kalis-letters": "Kali's Letters" };
const FILTERS = [["", "All works"], ["holy-book", "Holy Book"], ["indic-civilisation", "Civilisation"], ["kalis-letters", "Kali's Letters"]];

export default function SearchPage() {
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState(params.get("q") || "");
  const [work, setWork] = useState(params.get("work") || "");
  const [mode, setMode] = useState(params.get("mode") || "semantic");
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => { inputRef.current?.focus(); }, []);

  const run = async (query, w, m) => {
    if (!query.trim()) { setResults(null); return; }
    setLoading(true);
    try {
      const endpoint = m === "keyword" ? "/search" : "/semantic-search";
      const { data } = await api.get(endpoint, { params: { q: query, work: w, limit: 40 } });
      setResults(data.results.map((r) => ({ ...r, id: r.id || r.doc_id })));
    } catch { setResults([]); } finally { setLoading(false); }
  };

  useEffect(() => {
    const query = params.get("q") || "";
    const w = params.get("work") || "";
    const m = params.get("mode") || "semantic";
    setQ(query); setWork(w); setMode(m);
    if (query) run(query, w, m);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  const push = (next) => setParams(next.q ? { q: next.q, ...(next.work ? { work: next.work } : {}), mode: next.mode } : {});
  const submit = (e) => { e.preventDefault(); push({ q, work, mode }); };
  const pickFilter = (w) => { setWork(w); push({ q, work: w, mode }); };
  const pickMode = (m) => { setMode(m); push({ q, work, mode: m }); };

  return (
    <div className="pt-16 lg:pt-[72px] min-h-screen bg-indic-indigo" data-testid="search-page">
      <section className="py-14 lg:py-20 border-b border-indic-card-border">
        <div className="mx-auto max-w-3xl px-4 sm:px-8">
          <Eyebrow>Universal Search</Eyebrow>
          <h1 className="mt-3 font-serif text-4xl lg:text-5xl font-semibold text-amber-50">Search the civilisation</h1>
          <div className="mt-6 inline-flex rounded-sm border border-indic-card-border p-1 bg-indic-card" data-testid="search-mode-toggle">
            {[["semantic", "By meaning"], ["keyword", "By keyword"]].map(([val, label]) => (
              <button key={val} onClick={() => pickMode(val)} data-testid={`mode-${val}`}
                className={`rounded-sm px-4 py-1.5 text-sm font-medium transition-colors ${mode === val ? "bg-indic-gold text-indic-indigo" : "text-slate-300 hover:text-amber-50"}`}>
                {label}
              </button>
            ))}
          </div>
          <form onSubmit={submit} className="mt-4 relative">
            <SearchIcon size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-indic-gold" />
            <input ref={inputRef} value={q} onChange={(e) => setQ(e.target.value)} placeholder="dharma, consciousness, Nalanda, karma…"
              className="w-full rounded-sm border border-indic-card-border bg-indic-card py-4 pl-12 pr-12 text-lg text-amber-50 placeholder:text-slate-500 focus:border-indic-gold/60 focus:outline-none" data-testid="search-input" />
            {q && <button type="button" onClick={() => { setQ(""); setParams({}); setResults(null); }} className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-500 hover:text-amber-50"><X size={18} /></button>}
          </form>
          <p className="mt-3 text-xs text-slate-500">{mode === "semantic" ? "Finds passages by meaning — try a phrase or question, not just a keyword." : "Classic full-text keyword search across the library."}</p>
          <div className="mt-4 flex flex-wrap gap-2">
            {FILTERS.map(([val, label]) => (
              <button key={val} onClick={() => pickFilter(val)} data-testid={`filter-${val || "all"}`}
                className={`rounded-full px-4 py-1.5 text-sm transition-colors ${work === val ? "bg-indic-gold text-indic-indigo" : "border border-indic-card-border text-slate-300 hover:border-indic-gold/40"}`}>
                {label}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="py-12">
        <div className="mx-auto max-w-3xl px-4 sm:px-8">
          {loading && <div className="text-center text-slate-400 py-16">Searching…</div>}
          {!loading && results && (
            <>
              <p className="text-sm text-slate-400 mb-6" data-testid="search-count">{results.length} result{results.length !== 1 ? "s" : ""} for “{params.get("q")}”</p>
              <div className="space-y-4">
                {results.map((r, ri) => (
                  <Link key={`${r.id}-${ri}`} to={`/read/${r.id}`} className="block rounded-sm border border-indic-card-border bg-indic-card p-5 hover:border-indic-gold/40 transition-colors" data-testid={`result-${r.id}`}>
                    <div className="flex items-center gap-2 text-xs font-sans uppercase tracking-wider text-indic-gold/80">
                      <span>{WORK_LABEL[r.work_slug] || r.work_slug}</span>
                      <span className="text-slate-600">·</span>
                      <span className="text-slate-500 normal-case tracking-normal">{r.group_title}</span>
                    </div>
                    <h3 className="mt-2 font-serif text-xl text-amber-50">{r.code ? `${r.code} — ` : ""}{r.title}</h3>
                    <p className="mt-2 text-sm text-slate-300/80 leading-relaxed">{r.snippet}</p>
                  </Link>
                ))}
                {results.length === 0 && <div className="text-center text-slate-400 py-16">No passages found. Try another term.</div>}
              </div>
            </>
          )}
          {!loading && !results && (
            <div className="text-center text-slate-500 py-16">
              <p className="font-serif text-xl">Search 1,100+ sections across the INDIC library.</p>
              <p className="mt-2 text-sm">Try: <button onClick={() => push({ q: "the nature of suffering", mode })} className="text-indic-gold underline">the nature of suffering</button>, <button onClick={() => push({ q: "consciousness and the self", mode })} className="text-indic-gold underline">consciousness and the self</button>, <button onClick={() => push({ q: "what is dharma", mode })} className="text-indic-gold underline">what is dharma</button></p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
